import { useRef, useState, type FormEvent } from 'react';
import { projectTypes } from '@/mocks/work';

const FORM_URL = 'https://readdy.ai/api/form/db7jvaitp9sllopf7gr0';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
type Status = 'idle' | 'sending' | 'success' | 'error';
type Errors = Partial<Record<'name' | 'email' | 'message', string>>;

const labelCls = 'font-label text-[11px] uppercase tracking-[0.16em] text-background-200';
const fieldCls = (bad?: string) => `mt-1 w-full border-0 border-b bg-transparent px-0 py-3 text-sm text-background-50 placeholder:text-foreground-400 focus:outline-none focus:ring-0 transition-colors ${bad ? 'border-primary-300' : 'border-background-50/25 focus:border-primary-300'}`;

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [chars, setChars] = useState(0);
  const sending = useRef(false);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (sending.current) return;
    const form = e.currentTarget;
    const data = new FormData(form);

    if (String(data.get('company_alt') || '').trim()) { setStatus('success'); return; }
    data.delete('company_alt');

    const name = String(data.get('name') || '').trim();
    const email = String(data.get('email') || '').trim();
    const message = String(data.get('message') || '').trim();
    const next: Errors = {};
    if (!name) next.name = 'Please add your name.';
    if (!email) next.email = 'Please add your email.';
    else if (!EMAIL_RE.test(email)) next.email = 'Please enter a valid email address.';
    if (!message) next.message = 'Please tell us a little about the project.';
    else if (message.length > 500) next.message = 'Please keep it under 500 characters.';
    setErrors(next);
    if (Object.keys(next).length) {
      setStatus('idle');
      const first = form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`);
      first?.focus();
      return;
    }

    sending.current = true;
    setStatus('sending');
    setFormError('');
    try {
      const body = new URLSearchParams();
      data.forEach((v, k) => { if (String(v).trim()) body.append(k, String(v)); });
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: unknown } } | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      const serverMsg = parsed?.meta?.message || parsed?.message
        || (typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '') || responseText;
      if (response.ok && parsed?.code === 'OK' && !/spam/i.test(serverMsg || '')) {
        setStatus('success');
        form.reset();
        setChars(0);
      } else {
        setStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    } finally {
      sending.current = false;
    }
  };

  if (status === 'success') {
    return (
      <div aria-live="polite" className="border-t border-background-50/15 pt-8">
        <span className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500 text-background-50 text-xl"><i className="ri-check-line" /></span>
        <p className="mt-6 text-2xl md:text-3xl font-bold tracking-[-0.03em] text-background-50">Thank you. Your message is with us.</p>
        <p className="mt-3 text-sm text-background-200 max-w-[380px]">A producer will read it personally and get back to you soon.</p>
        <button type="button" onClick={() => setStatus('idle')} className="mt-8 font-label text-[11px] uppercase tracking-[0.16em] text-primary-300 hover:text-background-50 transition-colors whitespace-nowrap cursor-pointer">
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form id="contact-inquiry" data-readdy-form onSubmit={onSubmit} noValidate className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
      <label className="block">
        <span className={labelCls}>Name *</span>
        <input name="name" type="text" required autoComplete="name" placeholder="Your full name" aria-invalid={!!errors.name} aria-describedby="err-name" className={fieldCls(errors.name)} />
        {errors.name && <span id="err-name" className="mt-2 block text-xs text-primary-300">{errors.name}</span>}
      </label>
      <label className="block">
        <span className={labelCls}>Email *</span>
        <input name="email" type="email" required autoComplete="email" placeholder="you@company.com" aria-invalid={!!errors.email} aria-describedby="err-email" className={fieldCls(errors.email)} />
        {errors.email && <span id="err-email" className="mt-2 block text-xs text-primary-300">{errors.email}</span>}
      </label>
      <label className="block">
        <span className={labelCls}>Project type</span>
        <select name="project_type" defaultValue={projectTypes[0]} className={`${fieldCls()} cursor-pointer [&>option]:text-foreground-950`}>
          {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>
      <label className="block">
        <span className={labelCls}>Expected timeline</span>
        <input name="timeline" type="text" placeholder="e.g. Spring 2027 (optional)" className={fieldCls()} />
      </label>
      <label className="block sm:col-span-2">
        <span className={labelCls}>Project description *</span>
        <textarea name="message" rows={4} maxLength={500} required onChange={(e) => setChars(e.target.value.length)} placeholder="Place, audience, ambition. Whatever you have so far." aria-invalid={!!errors.message} aria-describedby="err-message" className={`${fieldCls(errors.message)} resize-none`} />
        <span className="mt-2 flex justify-between gap-4 text-xs">
          <span id="err-message" className="text-primary-300">{errors.message}</span>
          <span className="font-label text-foreground-400">{chars}/500</span>
        </span>
      </label>
      <input type="text" name="company_alt" className="ff-alt-field" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

      <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <button type="submit" disabled={status === 'sending'} className="inline-flex w-fit items-center justify-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-sm font-semibold text-background-50 hover:bg-background-50 hover:text-foreground-950 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer">
          {status === 'sending' ? 'Sending...' : 'Send inquiry'}
          <span className="w-4 h-4 flex items-center justify-center"><i className={status === 'sending' ? 'ri-loader-4-line animate-spin' : 'ri-arrow-right-line'} /></span>
        </button>
        <div aria-live="polite" className="text-sm">
          {status === 'error' && <p className="text-primary-300 font-medium">{formError}</p>}
        </div>
      </div>
    </form>
  );
}